import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DollarSign, Calculator, Printer, Check, Clock, Loader2 } from "lucide-react";
import { calculatePayslip, printPayslip } from "./constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PayslipDialog = ({
  open,
  onOpenChange,
  activeEmployees,
  payslipEmployee,
  setPayslipEmployee,
  payslipData,
  setPayslipData,
  generatedPayslip,
  setGeneratedPayslip,
  onGenerate,
}) => {
  // Real-time payslip calculation
  const livePayslipCalc = useMemo(() => {
    const selectedEmp = activeEmployees.find(e => e.id === payslipEmployee);
    return calculatePayslip(selectedEmp, payslipData);
  }, [payslipEmployee, payslipData, activeEmployees]);

  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [attendanceSummary, setAttendanceSummary] = useState(null);

  const handleLoadFromAttendance = async () => {
    if (!payslipEmployee) { toast.error("Select an employee first"); return; }
    if (!payslipData.pay_period_start || !payslipData.pay_period_end) {
      toast.error("Set both pay period dates first"); return;
    }
    setLoadingAttendance(true);
    try {
      const res = await axios.get(`${API}/employees/attendance/summary`, {
        params: {
          employee_id: payslipEmployee,
          start_date: payslipData.pay_period_start,
          end_date: payslipData.pay_period_end
        }
      });
      const s = res.data || {};
      setPayslipData({
        ...payslipData,
        hours_worked: s.hours_worked || 0,
        overtime_hours: s.overtime_hours || 0
      });
      setAttendanceSummary(s);
      toast.success(
        `Loaded ${s.hours_worked}h + ${s.overtime_hours}h OT across ${s.days_worked} day(s). ` +
        `${s.days_not_clocked} day(s) not clocked (unpaid).`
      );
    } catch (err) {
      console.error("Attendance summary failed", err);
      toast.error(err?.response?.data?.detail || "Failed to load attendance");
    } finally {
      setLoadingAttendance(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Generate Payslip
          </DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-6">
          {/* Left Column - Input Fields */}
          <div className="space-y-4">
            <div>
              <Label>Employee *</Label>
              <Select value={payslipEmployee} onValueChange={setPayslipEmployee}>
                <SelectTrigger data-testid="payslip-employee-select">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {[...activeEmployees].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} - R{emp.basic_salary}/{emp.salary_type === 'monthly' ? 'mo' : 'hr'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Pay Period Start *</Label>
                <Input 
                  type="date"
                  value={payslipData.pay_period_start}
                  onChange={(e) => setPayslipData({...payslipData, pay_period_start: e.target.value})}
                  data-testid="payslip-period-start"
                />
              </div>
              <div>
                <Label className="text-xs">Pay Period End *</Label>
                <Input 
                  type="date"
                  value={payslipData.pay_period_end}
                  onChange={(e) => setPayslipData({...payslipData, pay_period_end: e.target.value})}
                  data-testid="payslip-period-end"
                />
              </div>
            </div>

            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs text-gray-600">Hours & Overtime</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleLoadFromAttendance}
                disabled={loadingAttendance}
                className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="load-from-attendance-btn"
              >
                {loadingAttendance ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Clock className="h-3 w-3 mr-1" />
                )}
                Load from Attendance
              </Button>
            </div>

            {attendanceSummary && (
              <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2 text-blue-800">
                <div>
                  <strong>{attendanceSummary.days_worked}</strong> day(s) worked ·{" "}
                  <strong>{attendanceSummary.hours_worked}h</strong> regular ·{" "}
                  <strong>{attendanceSummary.overtime_hours}h</strong> OT
                </div>
                {attendanceSummary.days_not_clocked > 0 && (
                  <div className="text-amber-700 mt-1">
                    {attendanceSummary.days_not_clocked} day(s) not clocked — unpaid
                  </div>
                )}
                {attendanceSummary.paid_leave_days > 0 && (
                  <div>{attendanceSummary.paid_leave_days} paid leave day(s)</div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Hours Worked {livePayslipCalc?.employee?.salary_type === 'hourly' && <span className="text-red-500">*</span>}</Label>
                <Input 
                  type="number"
                  value={payslipData.hours_worked}
                  onChange={(e) => setPayslipData({...payslipData, hours_worked: parseFloat(e.target.value) || 0})}
                  placeholder="For hourly employees"
                  data-testid="payslip-hours-worked"
                />
              </div>
              <div>
                <Label className="text-xs">Overtime Hours</Label>
                <Input 
                  type="number"
                  value={payslipData.overtime_hours}
                  onChange={(e) => setPayslipData({...payslipData, overtime_hours: parseFloat(e.target.value) || 0})}
                  data-testid="payslip-overtime-hours"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Bonus (R)</Label>
                <Input 
                  type="number"
                  value={payslipData.bonus}
                  onChange={(e) => setPayslipData({...payslipData, bonus: parseFloat(e.target.value) || 0})}
                  data-testid="payslip-bonus"
                />
              </div>
              <div>
                <Label className="text-xs">Commission (R)</Label>
                <Input 
                  type="number"
                  value={payslipData.commission}
                  onChange={(e) => setPayslipData({...payslipData, commission: parseFloat(e.target.value) || 0})}
                  data-testid="payslip-commission"
                />
              </div>
              <div>
                <Label className="text-xs">Other Earnings (R)</Label>
                <Input 
                  type="number"
                  value={payslipData.other_earnings}
                  onChange={(e) => setPayslipData({...payslipData, other_earnings: parseFloat(e.target.value) || 0})}
                  data-testid="payslip-other-earnings"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Loan Deduction (R)</Label>
                <Input 
                  type="number"
                  value={payslipData.loan_deduction}
                  onChange={(e) => setPayslipData({...payslipData, loan_deduction: parseFloat(e.target.value) || 0})}
                  data-testid="payslip-loan-deduction"
                />
              </div>
              <div>
                <Label className="text-xs">Other Deductions (R)</Label>
                <Input 
                  type="number"
                  value={payslipData.other_deductions}
                  onChange={(e) => setPayslipData({...payslipData, other_deductions: parseFloat(e.target.value) || 0})}
                  data-testid="payslip-other-deductions"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Deduction Note</Label>
              <Input 
                type="text"
                value={payslipData.other_deductions_description || ""}
                onChange={(e) => setPayslipData({...payslipData, other_deductions_description: e.target.value})}
                placeholder="e.g., Advance repayment, Damage deduction..."
                data-testid="payslip-deduction-note"
              />
            </div>
          </div>

          {/* Right Column - Live Calculation Preview */}
          <div className="space-y-3">
            {livePayslipCalc ? (
              <>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-3">
                    <h4 className="font-semibold text-blue-800 text-sm mb-2">
                      {livePayslipCalc.employee.first_name} {livePayslipCalc.employee.last_name}
                    </h4>
                    <p className="text-xs text-blue-700">
                      {livePayslipCalc.employee.position} • {livePayslipCalc.employee.salary_type === 'monthly' ? 'Monthly Salary' : 'Hourly Rate'}: R {livePayslipCalc.employee.basic_salary?.toFixed(2)}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="py-2 px-3 bg-green-50">
                    <CardTitle className="text-sm text-green-700">Earnings</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Basic Pay:</span>
                      <span className="font-medium">R {livePayslipCalc.basicPay.toFixed(2)}</span>
                    </div>
                    {payslipData.overtime_hours > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Overtime ({payslipData.overtime_hours} hrs):</span>
                        <span className="font-medium">R {livePayslipCalc.overtimePay.toFixed(2)}</span>
                      </div>
                    )}
                    {payslipData.bonus > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Bonus:</span>
                        <span className="font-medium">R {payslipData.bonus.toFixed(2)}</span>
                      </div>
                    )}
                    {payslipData.commission > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Commission:</span>
                        <span className="font-medium">R {payslipData.commission.toFixed(2)}</span>
                      </div>
                    )}
                    {payslipData.other_earnings > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Other:</span>
                        <span className="font-medium">R {payslipData.other_earnings.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t pt-1 mt-1 font-bold">
                      <span>Gross Earnings:</span>
                      <span className="text-green-700">R {livePayslipCalc.grossEarnings.toFixed(2)}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="py-2 px-3 bg-red-50">
                    <CardTitle className="text-sm text-red-700">Deductions</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">PAYE Tax:</span>
                      <span className="font-medium text-red-600">R {livePayslipCalc.payeDeduction.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">UIF (1%):</span>
                      <span className="font-medium text-red-600">R {livePayslipCalc.uifDeduction.toFixed(2)}</span>
                    </div>
                    {payslipData.loan_deduction > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Loan:</span>
                        <span className="font-medium text-red-600">R {payslipData.loan_deduction.toFixed(2)}</span>
                      </div>
                    )}
                    {payslipData.other_deductions > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Other:</span>
                        <span className="font-medium text-red-600">R {payslipData.other_deductions.toFixed(2)}</span>
                      </div>
                    )}
                    {payslipData.other_deductions_description && (
                      <div className="text-xs text-gray-500 italic border-t pt-1 mt-1">
                        Note: {payslipData.other_deductions_description}
                      </div>
                    )}
                    <div className="flex justify-between border-t pt-1 mt-1 font-bold">
                      <span>Total Deductions:</span>
                      <span className="text-red-700">R {livePayslipCalc.totalDeductions.toFixed(2)}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-green-100 border-green-300">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-gray-600 mb-1">Net Pay</p>
                    <p className="text-3xl font-bold text-green-700" data-testid="payslip-net-pay">R {livePayslipCalc.netPay.toFixed(2)}</p>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="bg-gray-50">
                <CardContent className="p-6 text-center text-gray-500">
                  <Calculator className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <p>Select an employee to see live payslip calculation</p>
                </CardContent>
              </Card>
            )}

            {generatedPayslip && (
              <Card className="bg-green-50 border-green-300">
                <CardContent className="p-3 text-center">
                  <Check className="h-5 w-5 mx-auto text-green-600 mb-1" />
                  <p className="text-sm text-green-700 font-medium">Payslip Generated & Saved!</p>
                  <Button 
                    className="mt-2" 
                    size="sm"
                    onClick={() => printPayslip(generatedPayslip)}
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => {
            onOpenChange(false);
            setGeneratedPayslip(null);
          }}>Close</Button>
          <Button 
            onClick={onGenerate} 
            className="bg-green-600 hover:bg-green-700"
            disabled={!payslipEmployee || !payslipData.pay_period_start || !payslipData.pay_period_end}
            data-testid="generate-payslip-btn"
          >
            <DollarSign className="h-4 w-4 mr-2" />
            Save & Generate Payslip
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
